<template>
    <div class="drawer drawer-end">
        <input id="plugin-config-drawer" type="checkbox" class="drawer-toggle" v-model="visible" />
        <div class="drawer-side">
            <label for="plugin-config-drawer" aria-label="close sidebar" class="drawer-overlay"></label>
            <div class="p-4 min-h-full bg-base-100 text-base-content w-96 md:w-[500px]">
                <div class="flex justify-between items-center mb-6">
                    <div class="flex items-center space-x-3">
                        <div class="avatar placeholder">
                            <div class="bg-neutral text-neutral-content rounded-full w-12">
                                <span class="text-xl">{{ pluginIcon }}</span>
                            </div>
                        </div>
                        <div>
                            <h2 class="text-xl font-bold">{{ plugin?.name || '未知插件' }}</h2>
                            <p class="text-sm text-base-content/70">{{ plugin?.description || '暂无描述' }}</p>
                        </div>
                    </div>
                    <button class="btn btn-sm btn-circle btn-ghost" @click="close">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <!-- 插件信息 -->
                <div class="card bg-base-200 shadow-sm mb-6">
                    <div class="card-body p-4">
                        <h3 class="card-title text-lg mb-3">插件信息</h3>
                        <div class="space-y-2">
                            <div class="flex justify-between">
                                <span class="text-sm text-base-content/70">版本</span>
                                <div class="badge badge-primary badge-sm">v{{ plugin?.version || '未知' }}</div>
                            </div>
                            <div class="flex justify-between">
                                <span class="text-sm text-base-content/70">状态</span>
                                <div :class="['badge', 'badge-sm','text-white', plugin?.status === 'active' ? 'badge-success' : 'badge-warning']">
                                    {{ plugin?.status === 'active' ? '在线' : '离线' }}
                                </div>
                            </div>
                            <div class="flex justify-between">
                                <span class="text-sm text-base-content/70">类型</span>
                                <span class="text-sm">{{ pluginType === 'tagSource' ? '标签源插件' : '操作台插件' }}</span>
                            </div>
                            <div class="flex justify-between">
                                <span class="text-sm text-base-content/70">标识</span>
                                <span class="text-sm font-mono">{{ plugin?.unique_id || '未知' }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 基础配置 -->
                <div class="card bg-base-100 shadow-sm mb-6">
                    <div class="card-body p-4">
                        <div class="flex items-center space-x-2 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <h3 class="card-title text-lg">基础配置</h3>
                        </div>
                        
                        <div class="grid grid-cols-1 gap-4">
                            <!-- 标签源插件特有配置 -->
                            <div v-if="pluginType === 'tagSource'" class="space-y-4">
                                <div class="form-control">
                                    <label class="label">
                                        <span class="label-text font-medium">匹配超时时间</span>
                                        <span class="label-text-alt text-xs">秒</span>
                                    </label>
                                    <input type="number" class="input input-bordered input-sm w-full" v-model="configForm.timeout" min="5" max="300" />
                                </div>
                            </div>

                            <div class="form-control">
                                <label class="label">
                                    <span class="label-text font-medium">重试次数</span>
                                </label>
                                <input type="number" class="input input-bordered input-sm w-full" v-model="configForm.retryCount" min="0" max="5" />
                            </div>

                            <div class="form-control">
                                <label class="label cursor-pointer">
                                    <span class="label-text font-medium">缓存结果</span>
                                    <input type="checkbox" class="toggle toggle-primary" v-model="configForm.cacheEnabled" />
                                </label>
                            </div>

                            <!-- 操作台插件特有配置 -->
                            <div v-if="pluginType === 'action'" class="space-y-4">
                                <div class="form-control">
                                    <label class="label cursor-pointer">
                                        <span class="label-text font-medium">批量操作模式</span>
                                        <input type="checkbox" class="toggle toggle-primary" v-model="configForm.batchMode" />
                                    </label>
                                </div>

                                <div class="form-control">
                                    <label class="label">
                                        <span class="label-text font-medium">最大并发数</span>
                                    </label>
                                    <input type="number" class="input input-bordered input-sm w-full" v-model="configForm.maxConcurrency" min="1" max="10" />
                                </div>

                                <div class="form-control">
                                    <label class="label cursor-pointer">
                                        <span class="label-text font-medium">自动备份</span>
                                        <input type="checkbox" class="toggle toggle-primary" v-model="configForm.autoBackup" />
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 插件自定义配置 -->
                <div v-if="plugin?.config_schema && plugin.config_schema.length > 0" class="card bg-gradient-to-br from-base-100 to-base-200 shadow-sm mb-6 border border-primary/20">
                    <div class="card-body p-4">
                        <div class="flex items-center space-x-2 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                            </svg>
                            <h3 class="card-title text-lg">自定义配置</h3>
                            <div class="badge badge-primary badge-sm">{{ plugin?.config_schema?.length || 0 }} 项</div>
                        </div>
                        
                        <div class="grid grid-cols-1 gap-4">
                            <div v-for="field in plugin?.config_schema || []" :key="field.name" class="form-control bg-base-100 rounded-lg p-3 border border-base-300">
                                <!-- 数字类型 -->
                                <div v-if="field.type === 'number'">
                                    <label class="label pb-2">
                                        <div class="flex items-center space-x-2">
                                            <span class="label-text font-medium text-base-content">{{ field.description || field.name }}</span>
                                            <div v-if="field.help_text" class="tooltip" :data-tip="field.help_text">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/60 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                        </div>
                                        <span v-if="field.default !== undefined" class="label-text-alt text-xs bg-base-200 px-2 py-1 rounded">默认: {{ field.default }}</span>
                                    </label>
                                    <input 
                                        type="number" 
                                        class="input input-bordered input-sm w-full focus:input-primary" 
                                        v-model.number="field.default"
                                        :placeholder="field.default?.toString() || '请输入数值'"
                                    />
                                </div>
                                
                                <!-- 布尔类型 -->
                                <div v-else-if="field.type === 'bool'">
                                    <label class="label cursor-pointer">
                                        <div class="flex flex-col items-start">
                                            <div class="flex items-center space-x-2">
                                                <span class="label-text font-medium text-base-content">{{ field.description || field.name }}</span>
                                                <div v-if="field.help_text" class="tooltip" :data-tip="field.help_text">
                                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/60 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <span v-if="field.default !== undefined" class="text-xs text-base-content/60 mt-1">默认: {{ field.default ? '开启' : '关闭' }}</span>
                                        </div>
                                        <input 
                                            type="checkbox" 
                                            class="toggle toggle-primary" 
                                            v-model="field.default"
                                        />
                                    </label>
                                </div>
                                
                                <!-- 插件列表多选类型 -->
                                <div v-else-if="field.type === 'plugin_list'">
                                    <label class="label pb-2">
                                        <div class="flex items-center space-x-2">
                                            <span class="label-text font-medium text-base-content">{{ field.description || field.name }}</span>
                                            <div v-if="field.help_text" class="tooltip" :data-tip="field.help_text">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/60 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                        </div>
                                        <span v-if="field.default !== undefined && Array.isArray(field.default)" class="label-text-alt text-xs bg-base-200 px-2 py-1 rounded">已选: {{ field.default.length }} 项</span>
                                    </label>
                                    <div v-if="pluginListLoading" class="flex items-center justify-center p-4">
                                        <span class="loading loading-spinner loading-sm mr-2"></span>
                                        <span class="text-sm text-base-content/60">加载插件列表中...</span>
                                    </div>
                                    <multi-select
                                        v-else
                                        :key="'plugin-select-' + field.name + '-' + availablePlugins.length"
                                        :options="availablePlugins"
                                        v-model="field.default"
                                        :placeholder="availablePlugins.length === 0 ? '暂无可选插件' : '请选择插件'"
                                        @change="onPluginSelectionChange(field, $event)"
                                    ></multi-select>
                                </div>

                                <!-- 列表多选类型 -->
                                <div v-else-if="field.type === 'list'">
                                    <label class="label pb-2">
                                        <div class="flex items-center space-x-2">
                                            <span class="label-text font-medium text-base-content">{{ field.description || field.name }}</span>
                                            <div v-if="field.help_text" class="tooltip" :data-tip="field.help_text">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/60 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                        </div>
                                        <span v-if="field.default !== undefined && Array.isArray(field.default)" class="label-text-alt text-xs bg-base-200 px-2 py-1 rounded">已选: {{ field.default.length }} 项</span>
                                    </label>
                                    <multi-select
                                        :key="'list-select-' + field.name"
                                        :options="getListOptions(field)"
                                        v-model="field.default"
                                        :placeholder="getListOptions(field).length === 0 ? '暂无可选项' : '请选择'"
                                        @change="onListSelectionChange(field, $event)"
                                    ></multi-select>
                                </div>

                                <!-- 字符串类型 -->
                                <div v-else>
                                    <label class="label pb-2">
                                        <div class="flex items-center space-x-2">
                                            <span class="label-text font-medium text-base-content">{{ field.description || field.name }}</span>
                                            <div v-if="field.help_text" class="tooltip" :data-tip="field.help_text">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/60 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                        </div>
                                        <span v-if="field.default !== undefined" class="label-text-alt text-xs bg-base-200 px-2 py-1 rounded">默认: {{ field.default }}</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        class="input input-bordered input-sm w-full focus:input-primary" 
                                        v-model="field.default"
                                        :placeholder="field.default?.toString() || '请输入内容'"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- 操作按钮 -->
                <div class="flex justify-between items-center space-x-2">
                    <div class="flex space-x-2">
                    </div>
                    <div class="flex space-x-2">
                        <button class="btn btn-outline btn-sm" @click="close">取消</button>
                        <button class="btn btn-primary btn-sm" @click="saveConfig">
                            <span class="loading loading-spinner loading-sm" v-if="saving"></span>
                            保存
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import MultiSelect from "@/components/iview/MultiSelect";

export default {
    name: 'PluginConfigDrawer',
    components: { MultiSelect },
    props: {
        plugin: {
            type: Object,
            default: () => ({})
        },
        pluginType: {
            type: String,
            default: 'tagSource'
        },
        show: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            visible: false,
            saving: false,
            jsonError: '',
            dynamicConfig: {},
            availablePlugins: [],
            pluginListLoading: false,
            openPluginDropdown: null,
            selected: [],
            // configForm: {
            //     timeout: 30,
            //     maxConcurrency: 3,
            //     cacheEnabled: false
            // },
            tagFields: [
                { id: 'title', name: '标题' },
                { id: 'artist', name: '艺术家' },
                { id: 'album', name: '专辑' },
                { id: 'albumartist', name: '专辑艺术家' },
                { id: 'year', name: '年份' },
                { id: 'genre', name: '风格' },
                { id: 'tracknumber', name: '音轨号' },
                { id: 'discnumber', name: '光盘号' },
                { id: 'lyrics', name: '歌词' },
                { id: 'cover', name: '封面' }
            ]
        }
    },
    computed: {
        pluginIcon() {
            if (this.pluginType === 'tagSource') {
                return '🏷️'
            } else {
                return '⚙️'
            }
        },
        configForm() {
            return this.plugin?.default_config || {}
        }
    },
    watch: {
        show(newVal) {
            this.visible = newVal
            if (newVal) {
                // 抽屉打开时立即加载插件列表，不等待loadConfig完成
                this.loadPluginList()
                this.loadConfig()
                this.setupDocumentClickListener()
            } else {
                this.removeDocumentClickListener()
            }
        },
        visible(newVal) {
            if (!newVal) {
                this.$emit('update:show', false)
                this.removeDocumentClickListener()
            }
        }
    },
    methods: {
        onPluginSelectionChange(field, selectedValues) {
            // 当插件选择发生变化时更新字段的默认值
            this.$set(field, 'default', selectedValues);
        },

        onListSelectionChange(field, selectedValues) {
            // 当列表选择发生变化时更新字段的默认值
            this.$set(field, 'default', selectedValues);
        },

        getListOptions(field) {
            // 从field.options获取预定义的选项列表
            if (!field.options || !Array.isArray(field.options)) {
                return [];
            }

            // 将options转换为MultiSelect组件需要的格式
            // 支持两种格式:
            // 1. 简单数组: ['option1', 'option2']
            // 2. 对象数组: [{id: 'opt1', name: '选项1'}, {id: 'opt2', name: '选项2'}]
            return field.options.map(option => {
                if (typeof option === 'string') {
                    return {
                        id: option,
                        name: option
                    };
                } else if (typeof option === 'object' && option.id && option.name) {
                    return {
                        id: option.id,
                        name: option.name
                    };
                }
                return null;
            }).filter(Boolean);
        },

        getFieldValue(field) {
            // 确保返回数组类型的值
            if (!Array.isArray(field.default)) {
                this.$set(field, 'default', []);
            }
            return field.default;
        },

        updateFieldValue(field, value) {
            // 更新field的default值
            this.$set(field, 'default', value);
        },

        loadConfig() {
            // 从插件数据加载配置
            if (this.plugin?.config) {
                try {
                    const config = typeof this.plugin.config === 'string' 
                        ? JSON.parse(this.plugin.config) 
                        : this.plugin.config
                    
                    this.configForm = this.plugin.default_config
                    
                    // 加载动态配置
                    if (this.plugin?.config_schema) {
                        this.plugin.config_schema.forEach(field => {
                            if (config[field.name] !== undefined) {
                                this.dynamicConfig[field.name] = config[field.name]
                            }

                            // 初始化plugin_list类型的字段
                            if (field.type === 'plugin_list') {
                                if (!field.default) {
                                    this.$set(field, 'default', [])
                                }
                            }

                            // 初始化list类型的字段
                            if (field.type === 'list') {
                                if (!field.default) {
                                    this.$set(field, 'default', [])
                                } else if (!Array.isArray(field.default)) {
                                    // 确保default是数组类型
                                    this.$set(field, 'default', [])
                                }
                            }
                        })
                    }
                    
                    // 不再在这里调用loadPluginList，因为在show watcher中已经调用了
                } catch (error) {
                    console.error('配置解析错误:', error)
                }
            }
        },

        async loadPluginList() {
            if (this.pluginListLoading) {
                return // 避免重复加载
            }
            
            this.pluginListLoading = true
            console.log('开始加载插件列表...')
            
            try {
                // 获取所有插件列表
                const [tagResponse] = await Promise.all([
                    this.$api.Task?.getResourceScript?.({ page_size: 100, plugin_type: 'tag_source' }) || Promise.resolve({ result: false, data: { items: [] } }),
                ])
                
                let allPlugins = []
                
                if (tagResponse.result) {
                    allPlugins = allPlugins.concat(tagResponse.data.items || [])
                }
                
                console.log('获取到的原始插件数据:', allPlugins)
                
                this.availablePlugins = allPlugins.map(item => ({
                        id: item['unique_id'] !== undefined ? String(item['unique_id']) : '',
                        name: item['name'] !== undefined ? String(item['name']) : ''
                    }))
                    
                console.log('处理后的插件列表:', this.availablePlugins)
                
                // 强制触发Vue的响应性更新
                this.$forceUpdate()
                
            } catch (error) {
                console.error('获取插件列表失败:', error)
                this.availablePlugins = []
            } finally {
                this.pluginListLoading = false
            }
        },

        togglePluginDropdown(fieldName) {
            if (this.openPluginDropdown === fieldName) {
                this.openPluginDropdown = null
            } else {
                this.openPluginDropdown = fieldName
                // 如果还没有加载插件列表，则加载
                if (this.availablePlugins.length === 0 && !this.pluginListLoading) {
                    this.loadPluginList()
                }
            }
        },

        togglePluginSelection(field, pluginId) {
            if (!Array.isArray(field.default)) {
                this.$set(field, 'default', [])
            }
            
            const index = field.default.indexOf(pluginId)
            if (index > -1) {
                field.default.splice(index, 1)
            } else {
                field.default.push(pluginId)
            }
        },

        isPluginSelected(selectedList, pluginId) {
            return Array.isArray(selectedList) && selectedList.includes(pluginId)
        },

        getSelectedPluginNames(selectedIds) {
            if (!Array.isArray(selectedIds)) return []
            return selectedIds.map(id => {
                const plugin = this.availablePlugins.find(p => p.unique_id === id)
                return plugin ? (plugin.display_name || plugin.name) : id
            }).filter(Boolean)
        },

        initDynamicConfig() {
            // 根据config_schema初始化动态配置的默认值
            if (this.plugin?.config_schema) {
                this.plugin.config_schema.forEach(field => {
                    if (field.default !== undefined) {
                        this.$set(this.dynamicConfig, field.name, field.default)
                    }
                })
            }
        },

        async testPlugin() {
            try {
                this.$myMsg.notify({
                    title: '测试插件中...',
                    type: 'info',
                    time: 2000
                })
                
                
                this.$myMsg.notify({
                    title: '插件测试成功',
                    type: 'success',
                    time: 2000
                })
            } catch (error) {
                this.$myMsg.notify({
                    title: '插件测试失败',
                    message: error.message,
                    type: 'error',
                    time: 3000
                })
            }
        },

        resetConfig() {
            this.configForm = {
                enabled: true,
                priority: 50,
                timeout: 30,
                autoLyrics: true,
                autoCover: true,
                enabledFields: ['title', 'artist', 'album'],
                batchMode: false,
                maxConcurrency: 3,
                autoBackup: true,
                debugLevel: 'none',
                retryCount: 2,
                cacheEnabled: true
            }
            
            // 重置动态配置为默认值
            this.initDynamicConfig()
        },

        async saveConfig() {
            this.saving = true
            try {
                const config = {
                    default_config: this.configForm,
                    config_schema: this.plugin?.config_schema
                }

                this.$emit('config-updated', {
                    plugin: this.plugin,
                    config: config,
                    enabled: this.configForm.enabled
                })

                this.close()
            } catch (error) {
                this.$myMsg.notify({
                    title: '保存失败',
                    message: error.message,
                    type: 'error',
                    time: 3000
                })
            } finally {
                this.saving = false
            }
        },

        close() {
            this.visible = false
        },

        setupDocumentClickListener() {
            document.addEventListener('click', this.handleDocumentClick)
        },

        removeDocumentClickListener() {
            document.removeEventListener('click', this.handleDocumentClick)
        },

        handleDocumentClick(event) {
            // 检查点击是否在下拉框内部
            const dropdownElement = event.target.closest('.dropdown')
            if (!dropdownElement) {
                this.openPluginDropdown = null
            }
        }
    }
}
</script>

<style scoped>
.h-screen-header {
    height: calc(100vh - 4rem);
}
</style> 